import { RefObject, useEffect, useRef } from "react";

interface TransitionEndOptions {
  /**
   * Arm the wait. `onEnd` fires once for each false→true edge of `active`, then
   * the hook disarms until `active` goes false→true again.
   */
  active: boolean;
  /** CSS property whose transition we wait on, e.g. "opacity" | "transform". */
  property: string;
  /**
   * Safety ceiling, not the primary clock. If the browser never emits
   * `transitionend` (reduced motion, a no-op/interrupted transition, a
   * backgrounded tab, an unmount mid-flight), `onEnd` still fires after this
   * long so the caller can never get stuck mid-transition. Set it a little
   * above the CSS duration.
   */
  fallbackMs: number;
  onEnd: () => void;
}

/**
 * Runs `onEnd` exactly once when the CSS transition of `property` on the element
 * referenced by `ref` actually completes — driven by the real `transitionend`
 * event rather than a `setTimeout` that guesses (and duplicates) the CSS
 * duration. The stylesheet stays the single source of truth for timing; JS only
 * reacts to it.
 *
 * Why this exists: hardcoding `setTimeout(DURATION_MS)` to match a CSS
 * transition creates two independent clocks for one animation. They start at
 * different instants and can be throttled or media-query-overridden apart, so
 * the JS side fires a frame early (content overlap flash) or late (empty/stale
 * flash). Reacting to the transition itself removes the drift by construction.
 */
export function useTransitionEnd(
  ref: RefObject<HTMLElement | null>,
  { active, property, fallbackMs, onEnd }: TransitionEndOptions,
) {
  // Keep the latest onEnd without re-arming the effect on every render, so the
  // callback always sees current props/state when the transition resolves.
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => {
    if (!active) return;
    const el = ref.current;

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      el?.removeEventListener("transitionend", handleEnd);
      onEndRef.current();
    };

    // Ignore transitions bubbling up from descendants and unrelated properties.
    const handleEnd = (event: TransitionEvent) => {
      if (event.target === el && event.propertyName === property) finish();
    };

    el?.addEventListener("transitionend", handleEnd);
    const timer = setTimeout(finish, fallbackMs);

    return () => {
      clearTimeout(timer);
      el?.removeEventListener("transitionend", handleEnd);
    };
  }, [active, property, fallbackMs, ref]);
}
